import { useState, useEffect } from "react"
import { Link } from "@tanstack/react-router"
import { Menu, Search, Sparkles, ArrowRight } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet"
import { useUIStore } from "@/store/ui.store"

export function Navbar() {
  const { isMobileMenuOpen, setMobileMenuOpen } = useUIStore()
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY > 20
          setIsScrolled((prev) => (prev !== scrolled ? scrolled : prev))
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full navbar transition-colors duration-200 ${
          isScrolled
            ? "bg-surface md:bg-surface/85 md:backdrop-blur-md border-b border-outline-variant/70 shadow-sm shadow-black/[0.02]"
            : "bg-surface md:bg-surface/60 md:backdrop-blur-sm border-b border-outline-variant/30"
        }`}
        style={{
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          WebkitTransform: "translate3d(0, 0, 0)",
          transform: "translate3d(0, 0, 0)",
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
        }}
      >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between pl-4 sm:pl-6 pr-1 sm:pr-6 lg:px-8">
        
        {/* Left — Brand */}
        <Link
          to="/"
          className="group flex items-center gap-1 font-sans text-2xl font-black tracking-tight text-on-surface select-none"
        >
          <span className="text-primary transition-transform duration-300 group-hover:scale-110">E</span>
          <span>NGA</span>
        </Link>

        {/* Right — Desktop Nav */}
        <nav className="hidden items-center gap-4 md:flex">
          <Link
            to="/login"
            className="flex items-center justify-center h-9 px-4 rounded-lg border border-[#be001e]/25 text-[#be001e] hover:bg-[#be001e]/5 text-[12px] font-bold transition-colors cursor-pointer"
          >
            Login
          </Link>

          <Button
            className="relative overflow-hidden h-9 px-4 rounded-lg bg-[#be001e] hover:bg-[#a0001a] text-white text-[12px] font-bold transition-colors cursor-pointer"
            asChild
          >
            <Link to="/explore/suppliers" className="flex items-center gap-1.5">
              <span>Explore</span>
            </Link>
          </Button>
        </nav>

        {/* Mobile — Hamburger + Sheet */}
        <div className="md:hidden">
          <Sheet open={isMobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                className="flex items-center justify-center rounded-lg p-2 text-secondary hover:text-on-surface hover:bg-surface-container-low transition-colors"
              >
                <Menu className="size-5" />
              </button>
            </SheetTrigger>

            <SheetContent side="right" className="bg-surface border-l border-outline-variant flex flex-col justify-between">
              <div>
                <SheetHeader>
                  <SheetTitle>
                    <span className="font-sans text-xl font-black tracking-tight text-on-surface select-none">
                      <span className="text-primary">E</span>NGA
                    </span>
                  </SheetTitle>
                </SheetHeader>

                <nav className="mt-8 flex flex-col gap-2">
                  <SheetClose asChild>
                    <Link
                      to="/explore/suppliers"
                      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-on-surface hover:bg-surface-container-low hover:text-primary transition-all"
                    >
                      <Search className="size-4 text-primary" />
                      Explore Suppliers & Products
                    </Link>
                  </SheetClose>

                  <SheetClose asChild>
                    <Link
                      to="/signup"
                      search={{ role: "buyer" }}
                      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-on-surface hover:bg-surface-container-low hover:text-primary transition-all"
                    >
                      <Sparkles className="size-4 text-primary" />
                      Register as Buyer
                    </Link>
                  </SheetClose>

                  <SheetClose asChild>
                    <Link
                      to="/signup"
                      search={{ role: "seller" }}
                      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-on-surface hover:bg-surface-container-low hover:text-primary transition-all"
                    >
                      <Sparkles className="size-4 text-primary" />
                      Register as Seller
                    </Link>
                  </SheetClose>
                </nav>
              </div>

              <div className="pt-6 border-t border-outline-variant space-y-3">
                <SheetClose asChild>
                  <Button className="w-full rounded-xl bg-primary hover:bg-[#a5001a] text-white font-bold py-2.5" asChild>
                    <Link to="/login">Login</Link>
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button variant="outline" className="w-full rounded-xl border-primary text-primary font-bold py-2.5 hover:bg-primary/5" asChild>
                    <Link to="/signup">Create Account</Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
    {/* Spacer to preserve layout flow with fixed navbar */}
    <div className="h-16 w-full shrink-0" aria-hidden="true" />
  </>
)
}

